import { useMemo, useState } from 'react';
import {
  Badge,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Popover,
  Select,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { FilterList as FilterIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import {
  ARCHIVE_CODES,
  BROADCAST_LABEL,
  FILTER_DEFAULTS,
  MP_LABEL,
  SEVERITY_ORDER,
  countActiveFilters,
  severityColor,
} from './msProductsModel';

const FLAGS = [
  { key: 'withForced', label: 'Есть принуд' },
  { key: 'withoutPlan', label: 'Без техкарты' },
  { key: 'withMinMax', label: 'Есть min/max' },
  { key: 'includeArchived', label: 'Показать архивные товары МС' },
];

/** Коды проблем со счётчиками из /diagnostics/summary (с учётом выбранного кабинета). */
export function useIssueOptions(summary, accountId) {
  return useMemo(() => {
    const labels = summary?.labels || {};
    const codes = Array.isArray(summary?.codes) ? summary.codes : [];
    return codes
      .map((entry) => {
        let total = Number(entry.total) || 0;
        let enabled = Number(entry.enabled) || 0;
        if (accountId) {
          const acc = (entry.by_account || []).find((a) => String(a.account_id) === String(accountId));
          total = Number(acc?.total) || 0;
          enabled = Number(acc?.enabled) || 0;
        }
        const meta = labels[entry.code] || {};
        return {
          code: entry.code,
          total,
          enabled,
          label: meta.label || entry.code,
          description: meta.description || '',
          severity: meta.severity || 'info',
        };
      })
      .filter((item) => item.total > 0)
      .sort((a, b) => ((SEVERITY_ORDER[a.severity] ?? 3) - (SEVERITY_ORDER[b.severity] ?? 3)) || (b.total - a.total));
  }, [summary, accountId]);
}

export function toggleProblemCode(filters, code) {
  const on = filters.problemCodes.includes(code);
  return {
    ...filters,
    problemCodes: on ? filters.problemCodes.filter((c) => c !== code) : [...filters.problemCodes, code],
    includeArchived: !on && ARCHIVE_CODES.includes(code) ? true : filters.includeArchived,
  };
}

export function MsProductFiltersButton({ filters, onChange, summary, summaryLoading, onRefreshSummary }) {
  const [anchor, setAnchor] = useState(null);
  const accounts = Array.isArray(summary?.accounts) ? summary.accounts : [];
  const visibleAccounts = filters.marketplace ? accounts.filter((a) => a.marketplace === filters.marketplace) : accounts;
  const options = useIssueOptions(summary, filters.accountId);
  const active = countActiveFilters(filters);
  const set = (patch) => onChange({ ...filters, ...patch });

  return (
    <>
      <Badge color="primary" badgeContent={active} invisible={!active}>
        <Button variant="outlined" startIcon={<FilterIcon />} onClick={(e) => setAnchor(e.currentTarget)}>
          Фильтры
        </Button>
      </Badge>
      <Popover
        open={Boolean(anchor)}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{ paper: { sx: { width: 380, maxWidth: '95vw', p: 2 } } }}
      >
        <Stack spacing={2}>
          <Stack direction="row" spacing={1.5}>
            <FormControl size="small" fullWidth>
              <InputLabel>Маркетплейс</InputLabel>
              <Select
                label="Маркетплейс"
                value={filters.marketplace}
                onChange={(e) => {
                  const marketplace = e.target.value;
                  const acc = accounts.find((a) => String(a.id) === String(filters.accountId));
                  set({ marketplace, accountId: acc && marketplace && acc.marketplace !== marketplace ? '' : filters.accountId });
                }}
              >
                <MenuItem value="">Любой</MenuItem>
                {Object.entries(MP_LABEL).map(([value, label]) => (
                  <MenuItem key={value} value={value}>{label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" fullWidth>
              <InputLabel>Кабинет</InputLabel>
              <Select
                label="Кабинет"
                value={filters.accountId}
                onChange={(e) => set({ accountId: e.target.value })}
              >
                <MenuItem value="">Любой</MenuItem>
                {visibleAccounts.map((acc) => (
                  <MenuItem key={acc.id} value={String(acc.id)}>
                    {MP_LABEL[acc.marketplace] || acc.marketplace} · {acc.name || acc.id}
                    {acc.active === false ? ' (неактивен)' : ''}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>

          <FormControl size="small" fullWidth>
            <InputLabel>Трансляция</InputLabel>
            <Select label="Трансляция" value={filters.broadcast} onChange={(e) => set({ broadcast: e.target.value })}>
              <MenuItem value="">Любая</MenuItem>
              <MenuItem value="on">Включена (хотя бы одна связь)</MenuItem>
              <MenuItem value="off">Выключена (хотя бы одна связь)</MenuItem>
            </Select>
          </FormControl>

          <Box>
            {FLAGS.map((flag) => (
              <FormControlLabel
                key={flag.key}
                sx={{ display: 'flex', mr: 0 }}
                control={(
                  <Checkbox
                    size="small"
                    checked={!!filters[flag.key]}
                    onChange={(e) => set({ [flag.key]: e.target.checked })}
                  />
                )}
                label={<Typography variant="body2">{flag.label}</Typography>}
              />
            ))}
          </Box>

          <Divider />

          <Box>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.5 }}>
              <Typography variant="subtitle2">Тип проблемы</Typography>
              <Tooltip title="Пересчитать диагностику">
                <span>
                  <IconButton size="small" onClick={onRefreshSummary} disabled={summaryLoading}>
                    {summaryLoading ? <CircularProgress size={16} /> : <RefreshIcon fontSize="small" />}
                  </IconButton>
                </span>
              </Tooltip>
            </Stack>
            {!options.length && (
              <Typography variant="body2" color="text.secondary">
                {summaryLoading ? 'Считаем…' : 'Проблем не найдено'}
              </Typography>
            )}
            <Box sx={{ maxHeight: 280, overflow: 'auto' }}>
              {options.map((item) => (
                <Tooltip key={item.code} title={item.description} placement="left">
                  <FormControlLabel
                    sx={{ display: 'flex', mr: 0, alignItems: 'center' }}
                    control={(
                      <Checkbox
                        size="small"
                        checked={filters.problemCodes.includes(item.code)}
                        onChange={() => onChange(toggleProblemCode(filters, item.code))}
                      />
                    )}
                    label={(
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ width: '100%' }}>
                        <Box
                          sx={{
                            width: 8,
                            height: 8,
                            borderRadius: '50%',
                            flexShrink: 0,
                            bgcolor: `${severityColor(item.severity) === 'default' ? 'grey' : severityColor(item.severity)}.main`,
                          }}
                        />
                        <Typography variant="body2" sx={{ flex: 1 }}>{item.label}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {item.total}{item.enabled ? ` · вкл ${item.enabled}` : ''}
                        </Typography>
                      </Stack>
                    )}
                    slotProps={{ typography: { sx: { flex: 1 } } }}
                  />
                </Tooltip>
              ))}
            </Box>
          </Box>

          <Stack direction="row" justifyContent="space-between">
            <Button onClick={() => onChange({ ...FILTER_DEFAULTS })} disabled={!active}>Сбросить</Button>
            <Button variant="contained" onClick={() => setAnchor(null)}>Готово</Button>
          </Stack>
        </Stack>
      </Popover>
    </>
  );
}

/** Активные фильтры чипами с крестиками. */
export function ActiveFilterChips({ filters, onChange, summary }) {
  const labels = summary?.labels || {};
  const accounts = Array.isArray(summary?.accounts) ? summary.accounts : [];
  const chips = [];
  if (filters.marketplace) {
    chips.push({ key: 'mp', label: MP_LABEL[filters.marketplace] || filters.marketplace, clear: { marketplace: '' } });
  }
  if (filters.accountId) {
    const acc = accounts.find((a) => String(a.id) === String(filters.accountId));
    chips.push({ key: 'acc', label: `Кабинет: ${acc?.name || filters.accountId}`, clear: { accountId: '' } });
  }
  if (filters.broadcast) {
    chips.push({ key: 'bc', label: BROADCAST_LABEL[filters.broadcast], clear: { broadcast: '' } });
  }
  for (const flag of FLAGS) {
    if (filters[flag.key]) chips.push({ key: flag.key, label: flag.label, clear: { [flag.key]: false } });
  }
  for (const code of filters.problemCodes) {
    chips.push({
      key: `p:${code}`,
      label: labels[code]?.label || code,
      color: severityColor(labels[code]?.severity),
      clear: { problemCodes: filters.problemCodes.filter((c) => c !== code) },
    });
  }
  if (!chips.length) return null;
  return (
    <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap" alignItems="center">
      {chips.map((chip) => (
        <Chip
          key={chip.key}
          size="small"
          variant="outlined"
          color={chip.color || 'default'}
          label={chip.label}
          onDelete={() => onChange({ ...filters, ...chip.clear })}
        />
      ))}
      {chips.length > 1 && (
        <Button size="small" onClick={() => onChange({ ...FILTER_DEFAULTS })}>Сбросить все</Button>
      )}
    </Stack>
  );
}
